import { type ReactNode } from 'react'
import { Sandwich, SandwichContent } from '@/components/Sandwich'
import {
  EmptyState,
  EmptyStateDescription,
  EmptyStateIllustration,
  EmptyStateTitle,
} from '@/components/EmptyState'
import { IconButton } from '@/components/IconButton'
import { Icon } from '@/components/Icon'
import {
  Dropdown,
  DropdownItem,
  DropdownItemTitle,
  DropdownTitle,
} from '@/components/Dropdown'
import { Button } from '@/components/Button'
import { ScrollShadow } from '@/components/ScrollShadow'
import { useTransform } from 'motion/react'
import { useScrollOffsets } from '@/hooks/element-hooks'
import { clamp } from '@/utils/math-util'
import styles from './DraftingView.module.css'
import { Amount } from '@/features/payouts/components/Amount'
import { byDesign } from '@/infra/design'
import imgMissingRolesUrl from '@/assets/misc/img_missing-roles.svg'
import WorkspaceRolesIllustration from '@/components/SpotIllustration/WorkspaceRolesIllustration'

export interface DraftingViewProps {
  children?: ReactNode
}

export function DraftingView({ children }: DraftingViewProps) {
  return (
    <Sandwich stretch>
      <SandwichContent stretch>
        <div className={styles.root}>{children}</div>
      </SandwichContent>
    </Sandwich>
  )
}

export interface DraftProps {
  approvedCount: number
  isDisabled: boolean
  onExportCsv: () => unknown
  onRemoveAll: () => unknown
  total: number
  canSubmit: boolean
  isSubmitPending: boolean
  onSubmit: () => unknown
  confirmModal: ReactNode
  children?: ReactNode
}

export function Draft({
  approvedCount,
  isDisabled,
  onExportCsv,
  onRemoveAll,
  total,
  canSubmit,
  isSubmitPending,
  onSubmit,
  confirmModal,
  children,
}: DraftProps) {
  const DELTA = 48 // Used to animate the scroll shadow
  const { connectRef: scrollRef, scrollYStart } = useScrollOffsets()
  const scrollProgress = useTransform(scrollYStart, (v) => clamp(v / DELTA))

  return (
    <div className={styles.draft}>
      <div className={styles.content}>
        <div className={styles.card}>
          <div className={styles.headerContainer}>
            <div className={styles.header}>
              <div className={styles.title}>
                Approved transactions
                <span className={styles.approvedCount}>({approvedCount})</span>
              </div>
              <div className={styles.headerActions}>
                <Dropdown
                  autoDismiss
                  isDisabled={isDisabled}
                  button={
                    <IconButton
                      name="more"
                      label="Draft actions"
                      isDisabled={isDisabled}
                    />
                  }
                >
                  <DropdownTitle>Actions</DropdownTitle>
                  <DropdownItem
                    onPress={onExportCsv}
                    icon={<Icon name="download" />}
                  >
                    <DropdownItemTitle>Download CSV</DropdownItemTitle>
                  </DropdownItem>
                  <DropdownItem
                    icon={<Icon name="close" />}
                    onPress={onRemoveAll}
                  >
                    <DropdownItemTitle>Remove all</DropdownItemTitle>
                  </DropdownItem>
                </Dropdown>
              </div>
            </div>
            <ScrollShadow lift borderless opacity={scrollProgress} />
          </div>

          <div className={styles.transactions} ref={scrollRef}>
            {children}
          </div>
        </div>
      </div>

      <div className={styles.footer}>
        <div className={styles.summary}>
          <div className={styles.summaryTitle}>Total</div>
          <div className={styles.summaryValue}>
            <Amount value={total} />
          </div>
        </div>
        <div className={styles.footerActions}>
          <Button
            label="Submit Payout"
            block
            variant="primary"
            isDisabled={!canSubmit}
            isPending={isSubmitPending}
            onPress={onSubmit}
          />
        </div>
      </div>
      {confirmModal}
    </div>
  )
}

export function EmptyDraft() {
  return (
    <div className={styles.emptyStateContainer}>
      <div className={styles.emptyState}>
        <EmptyState size="sm">
          <EmptyStateIllustration
            src={imgMissingRolesUrl}
            illustration={byDesign(undefined, WorkspaceRolesIllustration)}
          />
          <EmptyStateTitle>No approved transactions</EmptyStateTitle>
          <EmptyStateDescription>
            There are no approved transactions. Use the table on the left to add
            some!
          </EmptyStateDescription>
        </EmptyState>
      </div>
    </div>
  )
}
